import { useRef, useState } from "react";
import { useHoldDigitizingBanner } from "../digitizingServiceStorage";
import { usePostHog } from "posthog-js/react";
import { getStoredEmail, setStoredEmail } from "../emailStorage";
import { EmailLinkForm } from "./EmailLinkForm";

interface EmailDesignModalProps {
  apiUrl: string;
  token: string;
  assetId: string;
  /** The format whose download button was clicked. Pre-checked. */
  format: string;
  /** Every format this design has files for, the clicked one included. */
  availableFormats: string[];
  prefilledEmail?: string;
  /** Which page/flow this capture happened on -- same idea as ShareButton's
   * own `source`, kept as a separate, slightly wider set of values here
   * since this modal (unlike ShareButton) is also reachable from the
   * replay pages. Required, not inferred, so a new call site can't
   * silently land in email_capture_submitted with no source at all. */
  source: "create" | "convert" | "digitize" | "replay" | "replay_upload";
  /** Opened from the "Email me this design" button rather than by a
   * download, so there may be no download in progress to mention. */
  requested?: boolean;
  onClose: () => void;
}

/** "DST", "DST and PES", "DST, PES and JEF" -- for use in a sentence. */
function spokenList(formats: string[]): string {
  const names = formats.map((f) => f.toUpperCase());
  if (names.length <= 1) return names.join("");
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/**
 * Offered after a download has already started, never before it -- or, once
 * that's capped (see emailPromptStorage), when asked for by its own button.
 *
 * The file is the thing the user came for and it is already on its way by the
 * time this renders, so every path out of here -- send, dismiss, backdrop,
 * Escape -- is allowed to be cheap. Nothing here gates the download.
 */
export function EmailDesignModal({
  apiUrl,
  token,
  assetId,
  format,
  availableFormats,
  prefilledEmail,
  source,
  requested,
  onClose,
}: EmailDesignModalProps) {
  // The digitizing service banner waits while this is on screen.
  useHoldDigitizingBanner();
  // Prefilled from a previous send, so a returning visitor emailing a second
  // design doesn't retype an address they already gave us once.
  const [email, setEmail] = useState(() => prefilledEmail || getStoredEmail());
  const [optIn, setOptIn] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState<{
    email: string;
    formats: string[];
    days: number;
    /** Anonymous, with profiles on: ask the visitor to log in to save it. */
    loginPrompt: boolean;
  } | null>(null);
  // The clicked format starts checked: it is the one they have already proved
  // they want, and an empty set would make the common case two clicks.
  const [selected, setSelected] = useState<string[]>([format]);

  // Undefined when the app renders without a PostHogProvider, i.e. when
  // BUN_PUBLIC_POSTHOG_KEY is unset (local dev). Call sites must guard.
  const posthog = usePostHog();

  // A double-click on the download button that opens this modal delivers
  // two click events at the same screen position. The first opens the
  // modal; the backdrop is a fixed, full-viewport overlay, so the second
  // lands on it instead of the button and would close the modal
  // immediately, reading as "clicked download, the prompt flashed and
  // vanished." Ignoring backdrop clicks in the first moment after mount
  // treats that second click as part of the same interaction rather than a
  // deliberate dismiss.
  const openedAtRef = useRef(Date.now());
  const handleBackdropClick = () => {
    if (Date.now() - openedAtRef.current < 300) return;
    onClose();
  };

  // Offer the clicked format even if the caller's list somehow lacks it --
  // rendering a dialog whose only checked box is missing would be worse.
  const choices = availableFormats.includes(format)
    ? availableFormats
    : [format, ...availableFormats];

  const toggle = (candidate: string) => {
    setSelected((current) =>
      current.includes(candidate)
        ? current.filter((f) => f !== candidate)
        : // Kept in the order the buttons are in, not the order clicked, so
          // the email lists formats the same way the page does.
          choices.filter((f) => f === candidate || current.includes(f)),
    );
  };

  const send = async () => {
    if (!email.trim()) {
      setError("Enter an email address");
      return;
    }
    if (selected.length === 0) {
      setError("Choose at least one format");
      return;
    }
    setSending(true);
    setError("");
    try {
      const response = await fetch(`${apiUrl}/api/${token}/email-design`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          asset_id: assetId,
          formats: selected,
          email: email.trim(),
          marketing_opt_in: optIn,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || "Could not send the email");
      }
      setSent({
        email: data.email,
        formats: data.formats ?? selected,
        days: data.link_valid_days,
        loginPrompt: data.login_prompt === true,
      });
      // Kept for prefill, here and in the sign-in form below. Only in this
      // browser: the server doesn't put a typed address on the token, so an
      // anonymous visitor stays anonymous until they log in.
      setStoredEmail(data.email);
      // $set attaches the address to this person's PostHog profile, not just
      // this one event -- marketing_opt_in is what makes this a sign-up
      // rather than a one-off delivery request, and it's the field a list
      // built from this event would filter on.
      posthog?.capture("email_capture_submitted", {
        source,
        asset_id: assetId,
        formats: data.formats ?? selected,
        // Whether this came from the "Email me this design" button rather than
        // the automatic post-download prompt.
        requested: Boolean(requested),
        marketing_opt_in: optIn,
        $set: { email: data.email },
      });
    } catch (err: any) {
      setError(err.message || "Could not send the email");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      onClick={handleBackdropClick}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Email this design"
        style={{
          background: "white",
          borderRadius: "8px",
          padding: "1.5rem",
          maxWidth: "26rem",
          width: "100%",
          boxShadow: "0 10px 40px rgba(0, 0, 0, 0.2)",
        }}
      >
        {sent ? (
          <>
            <h3 style={{ marginTop: 0 }}>On its way</h3>
            <p>
              We sent your {spokenList(sent.formats)}{" "}
              {sent.formats.length === 1 ? "file" : "files"} to{" "}
              <strong>{sent.email}</strong>. The download{" "}
              {sent.formats.length === 1 ? "link" : "links"} in that email work
              {sent.formats.length === 1 ? "s" : ""} for {sent.days} days.
            </p>
            {/* Asked of every anonymous visitor, whether or not the address
                already has an account: saying which would let anyone check
                who uses the site, which /api/login/email is careful not to. */}
            {sent.loginPrompt && (
              <div className="email-design-login">
                <p>
                  This design is only saved in this browser for now. Log in to
                  save it to your account.
                </p>
                <EmailLinkForm apiUrl={apiUrl} submitLabel="Email me a sign-in link" />
                <p style={{ fontSize: "0.85rem", margin: "0 0 0.75rem" }}>
                  Or <a href="/login">log in with Google</a>.
                </p>
              </div>
            )}
            <button onClick={onClose} style={primaryButton}>
              Done
            </button>
          </>
        ) : (
          <>
            <h3 style={{ marginTop: 0 }}>Want this emailed to you?</h3>
            <p style={{ color: "#555", fontSize: "0.9rem" }}>
              {requested
                ? "We'll send the files to your inbox so they are easy to find later."
                : "Your download has already started. We can also send the files to your inbox so they are easy to find later."}
            </p>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !sending && send()}
              placeholder="you@example.com"
              autoFocus
              style={{
                width: "100%",
                padding: "0.6rem",
                fontSize: "1rem",
                border: "1px solid #ccc",
                borderRadius: "4px",
                marginBottom: "0.75rem",
                boxSizing: "border-box",
              }}
            />
            <fieldset
              style={{
                border: "1px solid #eee",
                borderRadius: "4px",
                padding: "0.6rem 0.75rem",
                margin: "0 0 0.75rem",
              }}
            >
              <legend style={{ fontSize: "0.85rem", color: "#555", padding: "0 0.25rem" }}>
                Formats to send
              </legend>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.25rem 1rem" }}>
                {choices.map((choice) => (
                  <label
                    key={choice}
                    style={{
                      display: "flex",
                      gap: "0.4rem",
                      alignItems: "center",
                      fontSize: "0.9rem",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selected.includes(choice)}
                      onChange={() => toggle(choice)}
                    />
                    <span>{choice.toUpperCase()}</span>
                  </label>
                ))}
              </div>
            </fieldset>
            <label
              style={{
                display: "flex",
                gap: "0.5rem",
                alignItems: "flex-start",
                fontSize: "0.85rem",
                color: "#555",
                marginBottom: "1rem",
              }}
            >
              <input
                type="checkbox"
                checked={optIn}
                onChange={(e) => setOptIn(e.target.checked)}
              />
              <span>Also send me occasional updates about new fonts and features.</span>
            </label>
            {error && (
              <p style={{ color: "#c00", fontSize: "0.85rem", marginTop: 0 }}>{error}</p>
            )}
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <button onClick={send} disabled={sending} style={primaryButton}>
                {sending ? "Sending..." : "Send it"}
              </button>
              <button onClick={onClose} style={secondaryButton}>
                No thanks
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const primaryButton: React.CSSProperties = {
  flex: 1,
  padding: "0.6rem 1rem",
  fontSize: "1rem",
  border: "none",
  borderRadius: "4px",
  background: "#333",
  color: "white",
  cursor: "pointer",
};

const secondaryButton: React.CSSProperties = {
  padding: "0.6rem 1rem",
  fontSize: "1rem",
  border: "1px solid #ccc",
  borderRadius: "4px",
  background: "white",
  color: "#333",
  cursor: "pointer",
};
